import type { ReactNode } from 'react';

/** One bold-labelled point in a horizontal-L people card. */
export type PeopleCardBullet = {
  label: string;
  text: ReactNode;
};

export type PeopleCardProps = {
  /** Person / subject name — the card headline. */
  name: ReactNode;
  /** Image src. Sits left (horizontal) or top (vertical). */
  photo?: string;
  /** Alt text for the photo. Defaults to a stringified name when possible. */
  photoAlt?: string;
  /** Sans eyebrow above the name. Shown on every variant except horizontal-L. */
  eyebrow?: ReactNode;
  /** Lead paragraph below the name. */
  body?: ReactNode;
  /** Bold-labelled points — rendered only on the horizontal-L (feature) variant. */
  bullets?: ReadonlyArray<PeopleCardBullet>;
  /** Image left + text right ('horizontal') or image top + text below ('vertical'). */
  orientation?: 'horizontal' | 'vertical';
  /** Card surface — light (Blue-01) or dark (Teal-06). */
  tone?: 'light' | 'dark';
  /** 'L' is the feature size; 'S' is the compact size. */
  size?: 'L' | 'S';
  /** Optional extra class for deck-local scoping. */
  className?: string;
};

/**
 * People Card — DS 26-MARKETING-HANDOVER · Cards › People Cards.
 * Eight variants: orientation (horizontal | vertical) × tone (light | dark)
 * × size (L | S). Horizontal-L is the rich "feature" card (big name + bullets);
 * the others are eyebrow + name + body.
 */
export function PeopleCard({
  name,
  photo,
  photoAlt,
  eyebrow,
  body,
  bullets,
  orientation = 'vertical',
  tone = 'light',
  size = 'L',
  className,
}: PeopleCardProps) {
  const isFeature = orientation === 'horizontal' && size === 'L';
  const showEyebrow = !isFeature && eyebrow != null && eyebrow !== '';
  const showBullets = isFeature && bullets != null && bullets.length > 0;
  const alt = photoAlt ?? (typeof name === 'string' ? name : '');

  const cls = [
    'sn-pcard',
    `sn-pcard--${orientation}`,
    `sn-pcard--${tone}`,
    `sn-pcard--${size === 'L' ? 'lg' : 'sm'}`,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <article className={cls}>
      {photo && (
        <div className="sn-pcard-media">
          <img src={photo} alt={alt} />
        </div>
      )}
      <div className="sn-pcard-body">
        <div className="sn-pcard-head">
          {showEyebrow && <p className="sn-pcard-eyebrow">{eyebrow}</p>}
          <h3 className={`sn-pcard-name${isFeature ? ' sn-pcard-name--lg' : ''}`}>{name}</h3>
        </div>
        {body && <p className="sn-pcard-text">{body}</p>}
        {showBullets && (
          <ul className="sn-pcard-bullets">
            {bullets!.map((b, i) => (
              <li key={i} className="sn-pcard-bullet">
                <p className="sn-pcard-bullet-label">{b.label}</p>
                <p className="sn-pcard-bullet-text">{b.text}</p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </article>
  );
}
